import type { NavLinkMatch } from './matchNavLink';

/** One claim on the page in view, made by a navigation link that matches it. */
export interface NavLinkClaim extends NavLinkMatch {
    /** Unique id of the link inside its navigation group. */
    id: string;
}

/**
 * Warn, in development, when a second claim is as good as the winner.
 *
 * Two links that match the page equally declare routes that match the same URL. The tie-break
 * keeps the outcome deterministic, but it hides the too broad route behind it.
 */
const warnOnTie = (winner: NavLinkClaim, claims: NavLinkClaim[]) => {
    if (process.env.NODE_ENV === 'production') {
        return;
    }

    const tied = claims.find(
        (claim) =>
            claim !== winner &&
            claim.isLegacyMatch === winner.isLegacyMatch &&
            claim.matchedUrlLength === winner.matchedUrlLength,
    );

    if (tied) {
        // eslint-disable-next-line no-console
        console.warn(
            `Two navigation links match the page equally: "${winner.id}" and "${tied.id}". The first claim keeps it. One of the two routes is too broad.`,
        );
    }
};

/**
 * Pick the one link of a navigation group that points at the page in view.
 *
 * Only a link that matches the page claims it, so the claims are few. A legacy match wins outright:
 * a matched URL length has no meaning for a page served by Angular JS. Otherwise the longest matched
 * URL wins, so a more specific link beats a shorter one. A tie keeps the first claim, so the
 * outcome is deterministic, and development gets a warning.
 *
 * @param claims The claims, in the order they were made.
 * @returns The winner, or undefined when nobody claims the page.
 */
export const pickCurrentNavLink = (claims: NavLinkClaim[]): NavLinkClaim | undefined => {
    const legacy = claims.find((claim) => claim.isLegacyMatch);

    if (legacy) {
        warnOnTie(legacy, claims);

        return legacy;
    }

    let winner: NavLinkClaim | undefined;

    for (const claim of claims) {
        if (claim.matchedUrlLength > (winner?.matchedUrlLength ?? -1)) {
            winner = claim;
        }
    }

    if (winner) {
        warnOnTie(winner, claims);
    }

    return winner;
};
